import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Quote,
  Users,
  Receipt,
  Banknote,
  Wallet,
  Landmark,
  BookOpen,
  Settings,
  LogOut,
  Shield,
  Sparkles,
} from "lucide-react";
import { useAuth, type PackageFeatures } from "../../lib/auth";

type NavItem = {
  to: string;
  icon: typeof Quote;
  label: string;
  /** Feature-vlag uit het pakket die aan moet staan, of undefined = altijd. */
  feature?: keyof PackageFeatures;
};

const navItems: NavItem[] = [
  { to: "/quotes", icon: Quote, label: "Offertes", feature: "quotes" },
  { to: "/invoices", icon: FileText, label: "Facturen" },
  { to: "/payable-invoices", icon: Wallet, label: "Te betalen facturen" },
  { to: "/payments", icon: Banknote, label: "Verkoop / inkomsten", feature: "payments" },
  { to: "/expenses", icon: Receipt, label: "Inkoop / uitgaven", feature: "expenses" },
  // Kasboek (kassa-dagboek): kas-omzet, extra uitgaven, ritvergoedingen.
  { to: "/kasboek", icon: BookOpen, label: "Kasboek", feature: "kasboek" },
  // Banktransacties: beschikbaar in alle pakketten (geen feature-gate).
  { to: "/banktransacties", icon: Landmark, label: "Banktransacties" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/contacts", icon: Users, label: "Contacten" },
  { to: "/settings", icon: Settings, label: "Instellingen" },
];

export function Sidebar() {
  const { logout, user, activeCompany, features } = useAuth();

  // Verberg nav-items waarvan de feature niet in het actieve pakket zit.
  const visibleItems = navItems.filter((item) => !item.feature || features[item.feature]);

  return (
    <aside className="w-64 h-screen sticky top-0 flex flex-col z-40 text-white">
      <div
        className="absolute inset-0 bg-slate-900/95 backdrop-blur-xl"
        style={{
          background:
            "linear-gradient(160deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.97) 100%)",
        }}
      />

      <div className="relative p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-brand-500/20">
            <Sparkles size={30} className="text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg tracking-tight">AIBoek</h1>
            <p className="text-xs text-slate-400">Slim boekhouden</p>
          </div>
        </div>
      </div>

      <nav className="relative flex-1 px-4 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                isActive
                  ? "bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-500/25"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <item.icon size={27} />
            {item.label}
          </NavLink>
        ))}
        {user?.role === "SUPERADMIN" && <AdminLink />}
      </nav>

      <div className="relative p-4 border-t border-white/10">
        {activeCompany && (
          <div className="mb-4 px-3 py-2 rounded-xl bg-white/5 border border-white/10">
            <p className="text-[0.625rem] text-slate-400 uppercase tracking-wider">Bedrijf</p>
            <p className="text-sm font-semibold truncate">{activeCompany.name}</p>
            {activeCompany.activePackage && (
              <p className="text-[0.625rem] text-slate-400 truncate">
                {activeCompany.activePackage.name}
              </p>
            )}
          </div>
        )}
        <button
          onClick={logout}
          className="flex w-full items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/5 hover:text-white transition-all"
        >
          <LogOut size={27} />
          Uitloggen
        </button>
      </div>
    </aside>
  );
}

/** "Superadmin"-link in de sidebar. Opent het beheerpaneel zonder het actieve
 *  bedrijf te wissen, zodat superadmin daarna direct weer terug kan naar het
 *  dashboard (geen valkuil). */
function AdminLink() {
  return (
    <NavLink
      to="/superadmin"
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
          isActive
            ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/25"
            : "text-slate-300 hover:bg-white/5 hover:text-white"
        }`
      }
    >
      <Shield size={27} />
      Superadmin
    </NavLink>
  );
}